// ================================================================================================
// lodash
import _ from "lodash";

// React components
import * as React from "react";
import moment from "moment";

// Material-UI imports.
import { Card, CardText } from 'material-ui/Card';
import Checkbox from 'material-ui/Checkbox';
import UnCheckedIcon from 'material-ui/svg-icons/toggle/check-box-outline-blank';

import LinearProgress from 'material-ui/LinearProgress';
import Toggle from 'material-ui/Toggle';
import TextField from 'material-ui/TextField';
import FontIcon from 'material-ui/FontIcon';
import { ToggleProps, CheckboxProps } from 'material-ui';

// internal components
import { DateFormat, Formatters } from "@Utils/Formats";
import {
    isDefined, IsNullOrEmpty,
    IsNullOrWhiteSpace, getBool
} from "@Utils/Micro";
import { ItemsStyle, GetReactStyle, getZDepthShadow } from "@Utils/Colors";
import { ErrorCatch } from "./ErrorCatch";
import { Button, ButtonValueType } from './Button';
import { popupStaticWindow } from '@Utils/Utils';
import { Markdown } from './Markdown/MarkdownLazy';
import { sanitizeUrl } from '@Utils/SanitizeURL';
import { isFunction } from '@Utils/Micro';

// ================================================================================================
interface IUserInput {
    /**
     * Whether the input is disabled or not.
     */
    isUserInput?: boolean;
}
// ------------------------------------------------------------------------------------------------
export enum InfoTipLayout {
    Default,
    BigIcon
}
// ------------------------------------------------------------------------------------------------
export interface FontSettings {
    size?: number;
    color?: string;
    weight?: "bold" | "normal";
}
// ------------------------------------------------------------------------------------------------
export interface AdvancedInfoTip {
    /**
     * Infotip key
     */
    key?: string;

    /**
     * Icon's image URI.
     */
    imageUrl?: string;
    /**
     * Font icon.
     */
    icon?: string;
    /**
     * Icon size 
     */
    iconSize?: number;

    /**
     * Title.
     */
    title?: string;
    /** Title url Link */
    titleUrlLink?: string;
    /** Title font settings */
    titleFontSettings?: FontSettings;
    /**
     * Subtitle.
     */
    subtitle?: string;
    /** Subtitle url Link  */
    subtitleUrlLink?: string;
    /** Subtitle font settings */
    subtitleFontSettings?: FontSettings;
    /**
     * Body.
     */
    text?: string;
    /** Title font settings */
    textFontSettings?: FontSettings;
    /**
     * Additional items.
     */
    items?: InfoTipItem[];
    /**
     * Override default background color
     */
    backgroundColor?: string;
    /**
     * Override default global font color
     */
    fontColor?: string;


    /*
    * Override default global Open icon for group type item
    */
    groupOpenIcon?: string;

    /*
    * Override default global Close icon for group type item
    */
    groupCloseIcon?: string;

    /*
    * Override default global groupIcon aligment 
    */
    groupAlignLeft?: boolean

}
// ------------------------------------------------------------------------------------------------
export interface InfoTipItem {
    /**
     * Item's type.
     */
    type: InfoTipItemType;
    /**
     * Item's label.
     */
    label: string;
    /**
     * Item's value.
     */
    value: any | string[][];
    /**
     * Items settings
     */
    settings?: any;
    /**
     * Items icon
     */
    icon?: string;

    /**
     * Items theming
     */
    style?: ItemsStyle;

    /**
     * Items theming
     */
    valueStyle?: ItemsStyle;

    /**
     * Additional items. 
     * Valid for groups
     */
    items?: InfoTipItem[];

    /*
     * Settings for Close/Open Icon & Icon aligment.
     * Valid for groups
    */
    groupOpenIcon?: string;
    groupCloseIcon?: string;
    groupAlignLeft?: boolean;

}
// ------------------------------------------------------------------------------------------------
export enum InfoTipItemType {
    checkbox = 'checkbox',
    percentage = 'percentage',
    button = 'button',
    datetime = 'datetime',
    table = 'table',
    switch = 'switch',
    text = 'text',
    image = 'image',
    group = 'group',
    markdown = 'markdown'
}
// ------------------------------------------------------------------------------------------------
export interface MapTableSettings {
    /**
     * Split the spaces of columns
     * Default: true
     */
    uniform?: boolean;

    /**
     * style specific columns (dictionary from index to style)
     */
    columnsStyle?: { [key: number]: ItemsStyle };
}

export interface MapDatetimeSettings {
    format?: string;
    seperateLine?: boolean;
}
export interface MapCheckboxSettings extends IUserInput { }
export interface MapSwitchSettings extends IUserInput { }
export interface MapTextSettings extends IUserInput { }
export interface MapPercentageSettings { }
export interface MapButtonSettings {
    pageUrl?: string;

    /**
     * Theme
     */
    borderRadius?: number;
    backgroundColor?: string;
    fontColor?: string;
    icon?: string;
    iconType?: ButtonValueType
    iconFontSize?: number;
    uppercase?: boolean;
    shadowLevel?: number;
    tooltip?: string;
}
export interface InfoTipItemGroupSettings {
    /**
     * Start Open
     * Default: false
     */
    startOpen?: boolean;
}
// ------------------------------------------------------------------------------------------------
export interface InfoTipsTheme {
    Layout: InfoTipLayout;
    BigIconSize?: number;
    FontColor: string;
    FontSize: number;
    BackgroundColor: string;
    BorderRadius: number; // In Pixel
    cardStyle?: React.CSSProperties;
    shadowLevel?: number;
    groupOpenIcon?: string;
    groupCloseIcon?: string;
    groupAlignLeft?: boolean;
}
// ------------------------------------------------------------------------------------------------
const stopEvent = (e: React.MouseEvent<{}>) => {
    if (_.isEmpty(e))
        return;
    // Stopping bubbling and prevent action.
    e.stopPropagation();
    e.preventDefault();
};

const updatePopup = (i: InfoTipItem, v: any) => {
    if (_.isEmpty(i))
        return;
    // Update item's value.
    i.value = v;
};

function createItem(
    i: InfoTipItem,
    idx: number,
    infoTip: AdvancedInfoTip,
    infoTheme: InfoTipsTheme,
    handlePopupDataChange: (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => void
): React.ReactNode {
    const fontColor = i.style?.fontColor ?? infoTip?.fontColor ?? infoTheme.FontColor;

    switch (i.type) {
        // Item is a checkbox element.
        case InfoTipItemType.checkbox: {
            //todo: differ bar styling from label styling

            // Get item settings.
            const settings = i.settings as MapCheckboxSettings;
            const isUserInput = !_.isEmpty(settings) && settings.isUserInput == true;

            let checkboxProps: CheckboxProps = {};
            if (isUserInput) {
                checkboxProps.defaultChecked = getBool(i.value);
            } else {
                checkboxProps.checked = getBool(i.value);
            }

            return <Checkbox
                key={'checkbox-' + idx}
                label={i.label}
                labelPosition='left'
                style={{
                    padding: '2px 4px',
                    cursor: isUserInput ? 'pointer' : 'context-menu'
                }}
                labelStyle={{
                    fontSize: infoTheme.FontSize,
                    fontWeight: 'normal',
                    marginBottom: 0,
                    marginRight: -12,
                    color: fontColor
                }}
                uncheckedIcon={<UnCheckedIcon style={{ fill: fontColor }} />}
                iconStyle={{ transform: 'scale(0.9)' }}
                onCheck={(e: React.MouseEvent<{}>, v: boolean) => {
                    // Stop event or update and send popup's data.
                    if (!isUserInput)
                        stopEvent(e);
                    else {
                        updatePopup(i, v);
                        handlePopupDataChange(i, true, infoTip, false);
                    }
                }}
                {...checkboxProps} />
        }

        // Item is a percentage element.
        case InfoTipItemType.percentage:
            let color = i.style?.percentageColor ?? undefined;

            return <div
                key={'percentage-' + idx}
                style={{
                    padding: (idx == infoTip?.items?.length - 1) ? 4 : '2px 4px',
                    fontSize: infoTheme.FontSize
                }}>
                <div style={{ paddingBottom: 2 }}>
                    {!_.isEmpty(i.label)
                        ? i.label + ' - ' + i.value + '%'
                        : i.value + '%'}
                </div>
                <LinearProgress
                    key={'progress-' + idx}
                    min={0}
                    max={100}
                    style={{
                        height: 7,
                        borderColor: fontColor,
                        borderStyle: "solid",
                        borderWidth: 1
                    }}
                    mode='determinate'
                    value={i.value}
                    color={isDefined(color) ? color : i.value <= 60 ? 'green'
                        : (i.value <= 80 ? 'orange' : 'red')} />
            </div>

        // Item is a button element.
        case InfoTipItemType.button: {
            let settings = i.settings as MapButtonSettings;
            if (settings == null)
                settings = {};

            if (settings.backgroundColor == null)
                settings.backgroundColor = "rgb(0, 188, 212)";

            if (settings.fontColor == null)
                settings.fontColor = "white";

            return <Button
                key={'button-' + idx}
                label={i.label}
                labelAutoSize={false}

                borderRadius={settings.borderRadius}
                shadowLevel={settings.shadowLevel}
                tooltip={settings.tooltip}

                icon={settings.icon}
                iconFontSize={settings.iconFontSize}
                iconType={settings.iconType}
                iconFontColor={settings.fontColor}

                style={{
                    backgroundColor: settings.backgroundColor,
                    color: settings.fontColor,
                    textTransform: settings.uppercase ? 'uppercase' : 'initial',
                    margin: 4,
                    paddingTop: 2,
                    paddingBottom: 2,
                    fontSize: infoTheme.FontSize,
                    height: i.style?.height ?? 20
                }}

                onClick={() => {
                    handlePopupDataChange(i, false, infoTip, false);

                    if (isDefined(settings) &&
                        !IsNullOrEmpty(settings.pageUrl))
                        popupStaticWindow(settings.pageUrl, true);
                }} />;
        }

        // Item is a Table element.
        case InfoTipItemType.table: {
            let settings = i.settings as MapTableSettings;
            let colWidth = undefined;

            if (!isDefined(settings) || !_.has(settings, "uniform") || settings.uniform) {
                colWidth = ((_.isArray(i.value) && i.value.length > 0) ? Math.floor(100 / i.value[0].length) : 0) + "%";
            }

            return <div
                key={'table-' + idx}>
                <table
                    key={'table-' + idx}
                    style={{
                        width: "100%",
                        padding: "2px 0"
                    }}>
                    <tbody>
                        {_.map(i.value, (v: string[], _i: number) => {
                            return <tr key={'tr-' + _i}>
                                {_.map(v, (_v: string, j: number) => {
                                    let tdStyle;
                                    if (_.has(settings?.columnsStyle, j)) {
                                        tdStyle = GetReactStyle(settings.columnsStyle[j]);
                                    }
                                    return (
                                        <td
                                            key={'tr-' + j}
                                            style={{
                                                padding: '0 4px',
                                                width: colWidth,
                                                wordBreak: 'break-word',
                                                ...tdStyle
                                            }}>
                                            {_v}
                                        </td>
                                    );
                                })}
                            </tr>
                        })}
                    </tbody>
                </table>
            </div>
        }

        // Item is a datetime element.
        case InfoTipItemType.datetime: {

            // Get item's settings.
            const settings = i.settings as MapDatetimeSettings;
            let format = DateFormat.Short;

            let seperateLine = true;
            if (isDefined(settings)) {
                seperateLine = isDefined(settings?.seperateLine) ? settings?.seperateLine : true;
            }

            if (!_.isEmpty(settings) &&
                !_.isEmpty(settings.format)) {

                // Set datetime's format to display.
                switch (settings.format.toLowerCase()) {
                    case 'short':
                        format = DateFormat.Short;
                        break;

                    case 'long':
                        format = DateFormat.Long;
                        break;

                    case 'iso':
                        format = DateFormat.ISO;
                        break;

                    case 'iso ms':
                        format = DateFormat.ISOMS;
                        break;

                    default:
                        break;
                }
            }
            let datetime = null;
            try {
                datetime = moment.utc(i.value).toDate();
            } catch (ex) { }

            return <div
                key={'datetime-' + idx}
                style={{
                    padding: '2px 4px',
                    display: 'flex',
                    flexDirection: seperateLine ? 'column' : 'row'
                }}>
                <div style={{ paddingBottom: 2, width: seperateLine ? null : "50%" }}>
                    {i.label}
                </div>
                <div style={{
                    padding: seperateLine ? 0 : '0 4px',
                    ...GetReactStyle(i.valueStyle)
                }}>
                    {Formatters.datetime(datetime, format)}
                </div>
            </div>
        }

        // Item is a switch element.
        case InfoTipItemType.switch: {

            // Get item's settings.
            const settings = i.settings as MapSwitchSettings;
            const isUserInput = !_.isEmpty(settings) && settings.isUserInput == true;

            let toggleProps: ToggleProps = {};
            if (isUserInput) {
                toggleProps.defaultToggled = getBool(i.value);
            } else {
                toggleProps.toggled = getBool(i.value);
            }

            return <Toggle
                key={'toggle-' + idx}
                label={i.label}
                style={{
                    padding: '2px 4px',
                    cursor: isUserInput ? 'pointer' : 'context-menu'
                }}
                labelStyle={{
                    fontSize: infoTheme.FontSize,
                    fontWeight: 'normal',
                    marginBottom: 0,
                    marginRight: -12,
                    color: fontColor
                }}
                iconStyle={{ transform: 'scale(0.75)' }}
                onToggle={(e: React.MouseEvent<{}>, isInputChecked: boolean) => {
                    // Stop event or update and send popup's data.
                    if (!isUserInput)
                        stopEvent(e);
                    else {
                        updatePopup(i, isInputChecked);
                        handlePopupDataChange(i, true, infoTip, false);
                    }
                }}
                {...toggleProps} />
        }

        case InfoTipItemType.text: {
            // Get item's settings.
            const settings = i.settings as MapTextSettings;
            const isUserInput = !_.isEmpty(settings) &&
                settings.isUserInput == true;

            if (!isUserInput)
                return <div
                    key={'text-' + idx}
                    style={{
                        padding: '2px 4px',
                        whiteSpace: "pre-wrap"
                    }}>
                    <div style={{ paddingBottom: 2 }}>
                        {i.label}
                    </div>
                    <div>
                        {i.value}
                    </div>
                </div>

            // Add textarea with send button.
            return <div
                key={'text-field-wrapper-' + idx}
                style={{ position: 'relative' }}>
                <TextField
                    fullWidth
                    multiLine
                    key={'text-field-' + idx}
                    id={'text-field-' + idx}
                    rowsMax={3}
                    floatingLabelFixed
                    floatingLabelText={i.label}
                    style={{
                        padding: '2px 4px',
                        marginTop: -15,
                        width: isUserInput
                            ? 'calc(100% - 30px)'
                            : 'calc(100% - 10px)'
                    }}
                    floatingLabelStyle={{
                        color: fontColor,
                        fontSize: Math.floor(infoTheme.FontSize / 0.75),
                        fontWeight: "normal",
                        lineHeight: '34px'
                    }}
                    textareaStyle={{
                        color: fontColor,
                        fontSize: infoTheme.FontSize,
                        width: 'calc(100% + 8px)'
                    }}
                    underlineDisabledStyle={{ borderBottom: '1px solid rgb(224, 224, 224)' }}
                    disabled={!isUserInput}
                    defaultValue={i.value}
                    onChange={(e: React.FormEvent<{}>, newValue: string) => {
                        updatePopup(i, newValue);
                        handlePopupDataChange(i, true, infoTip, false);
                    }} />
                <FontIcon
                    className='fa fa-paper-plane'
                    style={{
                        transform: 'scale(0.65)',
                        cursor: 'pointer',
                        float: 'right',
                        position: 'absolute',
                        bottom: 10,
                        paddingLeft: 4,
                        color: fontColor
                    }}
                    onClick={() => handlePopupDataChange(i, false, infoTip, false)} />
            </div>
        }

        case InfoTipItemType.image: {
            return <img
                style={{
                    objectFit: "contain",
                    width: "100%",
                    ...GetReactStyle(i.style)
                }}
                src={i.value} />;
        }

        case InfoTipItemType.markdown: {
            return <Markdown
                style={{ padding: 5 }}
                source={i.value} />
        }

        default:
            return null;
    }
}

// ------------------------------------------------------------------------------------------------

export function getIcon(size: number,
    infoTip: AdvancedInfoTip,
    style: React.CSSProperties,
    handlePopupDataChange: (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => void
): JSX.Element {
    let iconSize = infoTip.iconSize ?? size;
    if (!_.isEmpty(infoTip.imageUrl))
        return <img
            style={{
                width: iconSize,
                height: iconSize,
                objectFit: 'contain',
                ...style
            }}
            src={infoTip.imageUrl}
            onClick={isDefined(handlePopupDataChange) ? () => handlePopupDataChange(null, false, infoTip, true) : undefined}
        />;
    else
        return <FontIcon
            style={{
                width: iconSize,
                height: iconSize,
                fontSize: iconSize,
                textAlign: "center",
                ...style
            }}
            className={infoTip.icon}
            onClick={isDefined(handlePopupDataChange) ? () => handlePopupDataChange(null, false, infoTip, true) : undefined} />;
}

// ------------------------------------------------------------------------------------------------

function renderText(text: string, settings: FontSettings, exStyle: React.CSSProperties = null, textUrlLink: string = null) {
    if (IsNullOrWhiteSpace(text))
        return null;

    let style = {
        display: "block",
        color: settings.color,
        fontWeight: settings.weight,
        fontSize: settings.size,
        width: 'initial',
        ...exStyle
    };

    if (!IsNullOrWhiteSpace(textUrlLink))
        return <a style={style} href={sanitizeUrl(textUrlLink)} rel="noopener noreferrer" target="_blank">
            {text}
        </a>

    return <span style={style}>{text}</span>
}

// ------------------------------------------------------------------------------------------------
export interface InfoTipItemComponentProps {
    infoTipItem: InfoTipItem;
    idx: number;
    infoTip: AdvancedInfoTip;
    infoTheme: InfoTipsTheme;
    handlePopupDataChange: (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => void;
    fontColor: string;
}
// ------------------------------------------------------------------------------------------------
export interface InfoTipItemComponentStates {
    isGroupClosed: boolean;
}
// ------------------------------------------------------------------------------------------------
export class InfoTipItemComponent extends React.Component<InfoTipItemComponentProps, InfoTipItemComponentStates> {
    constructor(props) {
        super(props);
        let startOpen = false;
        if (props.infoTipItem?.type == "group") {
            startOpen = props.infoTipItem?.settings?.startOpen ?? false;
        }

        this.state = {
            isGroupClosed: !startOpen
        };
    }
    // --------------------------------------------------------------------------------------------
    render() {
        let {
            infoTipItem,
            idx,
            infoTip,
            infoTheme,
            handlePopupDataChange,
            fontColor
        } = this.props;

        if (!isDefined(infoTipItem))
            return null;

        let fontSize = infoTheme.FontSize;
        let style: React.CSSProperties = GetReactStyle(infoTipItem.style);
        let localFontColor = infoTipItem?.style?.fontColor ?? fontColor;
        let result = null;

        let bodyItem = null;
        if (infoTipItem.type == InfoTipItemType.group) {
            let groupOpenIcon = infoTipItem?.groupOpenIcon ?? infoTip?.groupOpenIcon ?? infoTheme.groupOpenIcon ?? 'fas fa-angle-down';
            let groupCloseIcon = infoTipItem?.groupCloseIcon ?? infoTip?.groupCloseIcon ?? infoTheme.groupCloseIcon ?? 'fas fa-angle-left';
            let groupIconAlignLeft = infoTipItem?.groupAlignLeft ?? infoTip?.groupAlignLeft ?? infoTheme.groupAlignLeft;

            bodyItem = <div
                className="settingslist-submenu"
                style={{
                    width: "auto",
                    cursor: "pointer",
                    paddingLeft: 0,
                    justifyContent: (groupIconAlignLeft) ? 'unset' : 'space-between'
                }}
                onClick={ev => {
                    ev.stopPropagation();
                    this.setState({ isGroupClosed: !this.state.isGroupClosed });
                }}
                onDoubleClick={ev => ev.stopPropagation()}>
                {(groupIconAlignLeft) ?
                    <span className="settingslist-submenu-icon-left" >
                        <i className={"fas " + (!this.state.isGroupClosed ? groupOpenIcon : groupCloseIcon)} />
                    </span> : null}
                <div
                    key={'text-' + idx}
                    style={{
                        padding: '2px 4px'
                    }}>

                    <div style={{ paddingBottom: 2, justifyContent: 'flex-start' }}>
                        {infoTipItem.label}
                    </div>
                    <div>
                        {infoTipItem.value}
                    </div>
                </div>
                {(groupIconAlignLeft) ? null :
                    <span className="settingslist-submenu-icon-left" >
                        <i className={"fas " + (!this.state.isGroupClosed ? groupOpenIcon : groupCloseIcon)} />
                    </span>}
            </div>
        } else {
            bodyItem = createItem(infoTipItem, idx, infoTip, infoTheme, handlePopupDataChange);
        }

        // Construct the container
        if (!_.isEmpty(infoTipItem.icon)) {
            result = <div
                key={"flex-div-" + idx}
                style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    margin: (infoTipItem.type == InfoTipItemType.group) ? "2px 0" : undefined,
                    ...style
                }}>
                <FontIcon style={{
                    width: fontSize,
                    height: fontSize,
                    fontSize: fontSize,
                    marginLeft: 5,
                    marginRight: 10,
                    color: localFontColor
                }} className={infoTipItem.icon} />
                <div style={{ flex: "auto" }}>
                    {bodyItem}
                </div>
            </div>
        } else {
            result = <div style={{
                margin: (infoTipItem.type == InfoTipItemType.group) ? "2px 0" : undefined,
                ...style
            }}> {bodyItem}</div>
        }

        if (infoTipItem.type != InfoTipItemType.group ||
            this.state.isGroupClosed)
            return result;
        return _.flatMap([
            result,
            _.map(infoTipItem.items, (x, idx) => <InfoTipItemComponent
                key={"item-" + idx}
                infoTipItem={x}
                idx={idx}
                infoTip={infoTip}
                infoTheme={infoTheme}
                handlePopupDataChange={handlePopupDataChange}
                fontColor={fontColor}
            />)
        ]);
    }
    // --------------------------------------------------------------------------------------------
}
// ------------------------------------------------------------------------------------------------
export interface InfoTipCoreProps extends React.Props<InfoTipCore> {
    width: number;
    infoTip: AdvancedInfoTip;
    infoTheme: InfoTipsTheme;
    handlePopupDataChange: (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => void;
    handleInfoTipClick?: (infoTip: AdvancedInfoTip, type: 'doubleClick' | 'singleClick') => void
}

export class InfoTipCore extends React.PureComponent<InfoTipCoreProps>
{
    render() {
        let {
            width,
            infoTip,
            infoTheme,
            handlePopupDataChange
        } = this.props;

        let key = infoTip.key;

        // Cannot pass empty container or info tip.
        if (_.isEmpty(key) ||
            _.isEmpty(infoTip))
            return null;

        const hasImage: boolean = !_.isEmpty(infoTip.imageUrl) || !_.isEmpty(infoTip.icon);
        const hasTitle: boolean = !IsNullOrWhiteSpace(infoTip.title) || !IsNullOrWhiteSpace(infoTip.subtitle);

        const backgroundColor = _.isEmpty(infoTip.backgroundColor) ? infoTheme.BackgroundColor : infoTip.backgroundColor;
        let fontColor = _.isEmpty(infoTip.fontColor) ? infoTheme.FontColor : infoTip.fontColor;

        /*
        *Configuring Settings for Text widget
        */
        const titleFontSettings = (isDefined(infoTip.titleFontSettings)) ?
            infoTip.titleFontSettings :
            {
                color: "inherit",
                size: infoTheme.FontSize + 2
            };
        const subtitleFontSettings = (isDefined(infoTip.subtitleFontSettings)) ?
            infoTip.subtitleFontSettings :
            {
                color: "inherit",
                size: infoTheme.FontSize + 1
            };

        const textFontSettings = (isDefined(infoTip.textFontSettings)) ?
            infoTip.textFontSettings :
            {
                color: "inherit",
                size: infoTheme.FontSize + 1
            };

        const boxShadow = infoTheme.shadowLevel != null ? getZDepthShadow(infoTheme.shadowLevel) : getZDepthShadow(1);
        switch (infoTheme.Layout) {
            case InfoTipLayout.Default:
                return <div
                    key={'card-' + key}
                    style={{
                        zIndex: 1,
                        fontSize: infoTheme.FontSize,
                        borderTopLeftRadius: infoTheme.BorderRadius,
                        borderTopRightRadius: infoTheme.BorderRadius,
                        borderBottomRightRadius: infoTheme.BorderRadius,
                        borderBottomLeftRadius: infoTheme.BorderRadius,
                        backgroundColor: backgroundColor,
                        color: fontColor,
                        padding: 4,
                        boxShadow: boxShadow,
                        ...infoTheme.cardStyle
                    }}
                    onDoubleClick={isFunction(this.props.handleInfoTipClick) ? () => this.props.handleInfoTipClick(infoTip, "doubleClick") : undefined}
                    onClick={isFunction(this.props.handleInfoTipClick) ? () => this.props.handleInfoTipClick(infoTip, "singleClick") : undefined}
                >
                    <ErrorCatch>
                        {hasTitle || hasImage
                            ? <div
                                style={{
                                    padding: "2px 4px",
                                    fontWeight: 500,
                                    boxSizing: "border-box",
                                    position: "relative",
                                    whiteSpace: "nowrap"
                                }}
                                onClick={() => handlePopupDataChange(null, false, infoTip, true)}>
                                {hasImage
                                    ? getIcon(40, infoTip,
                                        {
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            marginRight: 14,
                                            color: fontColor
                                        }, null)
                                    : null}
                                {hasTitle
                                    ? <div style={{
                                        display: "inline-block",
                                        verticalAlign: "top",
                                        whiteSpace: "normal",
                                        paddingRight: 8,
                                        width: hasImage ? 'calc(100% - 40px)' : 'initial'
                                    }}>
                                        {renderText(infoTip.title, titleFontSettings, null, infoTip.titleUrlLink)}
                                        {renderText(infoTip.subtitle, subtitleFontSettings, { opacity: 0.6 }, infoTip.subtitleUrlLink)}
                                    </div>
                                    : null}
                            </div> : null}
                        {renderText(infoTip.text, textFontSettings, { padding: "2px 4px" })}
                        {_.map(infoTip.items, (i: InfoTipItem, idx: number) => <InfoTipItemComponent
                            key={"item-" + idx}
                            infoTipItem={i}
                            idx={idx}
                            infoTip={infoTip}
                            infoTheme={infoTheme}
                            handlePopupDataChange={handlePopupDataChange}
                            fontColor={fontColor}
                        />)}
                    </ErrorCatch>
                </div>
            case InfoTipLayout.BigIcon:
                return <Card
                    key={'card-' + key}
                    style={{
                        fontSize: infoTheme.FontSize,
                        borderTopLeftRadius: infoTheme.BorderRadius,
                        borderTopRightRadius: infoTheme.BorderRadius,
                        borderBottomRightRadius: infoTheme.BorderRadius,
                        borderBottomLeftRadius: infoTheme.BorderRadius,
                        backgroundColor: backgroundColor,
                        color: fontColor,
                        padding: 4,
                        ...infoTheme.cardStyle
                    }}
                >
                    <ErrorCatch>
                        <CardText
                            key={"card-text-" + key}>
                            <div
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    color: fontColor
                                }}>
                                {getIcon((isDefined(infoTheme.BigIconSize)) ? infoTheme.BigIconSize * width : 0.75 * width,
                                    infoTip,
                                    { color: fontColor },
                                    handlePopupDataChange)}
                            </div>
                            {renderText(infoTip.title, titleFontSettings, { textAlign: "center" }, infoTip.titleUrlLink)}
                            {renderText(infoTip.subtitle, subtitleFontSettings, { textAlign: "center", opacity: 0.6 }, infoTip.subtitleUrlLink)}
                            {renderText(infoTip.text, textFontSettings, { padding: "2px 4px" })}
                        </CardText>
                        {
                            _.map(infoTip.items, (i: InfoTipItem, idx: number) => <InfoTipItemComponent
                                key={"item-" + idx}
                                infoTipItem={i}
                                idx={idx}
                                infoTip={infoTip}
                                infoTheme={infoTheme}
                                handlePopupDataChange={handlePopupDataChange}
                                fontColor={fontColor}
                            />)
                        }
                    </ErrorCatch>
                </Card>
        }
    }
}

// ------------------------------------------------------------------------------------------------

export function renderInfoTip(
    key: string,
    width: number,
    infoTip: AdvancedInfoTip,
    infoTheme: InfoTipsTheme,
    handlePopupDataChange: (i: InfoTipItem, rerender: boolean, infoTip: AdvancedInfoTip, iconClicked: boolean) => void,
    handleInfoTipClick?: (infoTip: AdvancedInfoTip) => void
): React.ReactNode {
    // Cannot pass empty container or info tip.
    if (_.isEmpty(key) || _.isEmpty(infoTip))
        return null;
    if (_.isEmpty(infoTip.key))
        infoTip.key = key;
    return <InfoTipCore
        key={key}
        width={width}
        infoTip={infoTip}
        infoTheme={infoTheme}
        handlePopupDataChange={handlePopupDataChange}
        handleInfoTipClick={handleInfoTipClick}
    />
}
// ------------------------------------------------------------------------------------------------
export function createPortStateFromInfoTipEvent(i: InfoTipItem, infoTip: AdvancedInfoTip, iconClicked: boolean): any {
    if (iconClicked)
        return {
            infoTip: infoTip,
            item: { type: "icon" }
        };
    else
        return {
            infoTip: infoTip,
            item: i,
            value: (isDefined(i)) ? i.value : null
        };
}
// ------------------------------------------------------------------------------------------------
// BACKWARDS COMPATIBILITY WARNING
// FOR NOW, DON'T UPDATE SCHEMA VALIDATOR AT OLD WIDGETS.
// USE IT ONLY AT NEW WIDGETS!
export const AdvancedInfoTipAjvSchema = {
    type: 'object',
    properties: {
        imageUrl: { type: 'string' },
        icon: { type: 'string' },
        title: { type: 'string' },
        subtitle: { type: 'string' },
        text: { type: 'string' },
        items: {
            type: 'array',
            items: {
                type: 'object',
                properties: {
                    type: {
                        type: 'string',
                        enum: ['checkbox', 'percentage', 'button', 'datetime', 'table', 'switch', 'text', 'image', 'group', 'markdown']
                    },
                    label: { type: 'string' },
                    value: {
                        type: ['string', 'number', 'boolean', 'object', 'array']
                    },
                    settings: {
                        type: 'object',
                        properties: {
                            format: { type: 'string' },
                            pageUrl: { type: 'string' },
                            isUserInput: { type: 'boolean' },
                        }
                    },
                    icon: { type: 'string' },
                },
                required: ['type', 'label']
            }
        },
        backgroundColor: {
            type: 'string',
        },
        fontColor: {
            type: 'string',
        }
    }
}
// ------------------------------------------------------------------------------------------------
